import { Component } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BaseCellRendererComponent } from '@examdojo/ag-grid';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { PayoutRequestUIModel, WorkingHoursDialogService } from '@examdojo/payments';
import { ICellRendererParams } from 'ag-grid-community';

@Component({
  selector: 'exam-payment-request-hours-cell-renderer',
  template: `
    <div class="flex h-full items-center gap-2">
      <span class="text-sm">{{ totalHours }}</span>
      @if (shouldShowButton) {
        <dojo-button
          [fill]="'outline'"
          [matTooltip]="'View submitted hours'"
          [size]="'xs'"
          (click)="openHoursDialog($event)"
          class="ml-2"
        >
          <dojo-icon [icon]="'external-link'" />
        </dojo-button>
      }
    </div>
  `,
  imports: [ButtonComponent, IconComponent, MatTooltipModule],
})
export class PaymentRequestHoursCellRendererComponent extends BaseCellRendererComponent<number, PayoutRequestUIModel> {
  constructor(private readonly workingHoursDialogService: WorkingHoursDialogService) {
    super();
  }

  private rowData: PayoutRequestUIModel | undefined;

  override agInit(params: ICellRendererParams<PayoutRequestUIModel, number>): void {
    super.agInit(params);
    // Capture the row data when the cell is initialized
    this.rowData = params.data;
  }

  override refresh(params: ICellRendererParams<PayoutRequestUIModel, number>): boolean {
    const result = super.refresh(params);
    // Update the captured row data when the cell is refreshed
    this.rowData = params.data;
    return result;
  }

  get totalHours(): string {
    return this.rowData?.totalHours?.toString() ?? '0';
  }

  get shouldShowButton(): boolean {
    return this.rowData?.status !== 'DRAFT';
  }

  async openHoursDialog(event: Event) {
    event.stopPropagation();

    if (!this.rowData) {
      return;
    }

    const payoutRequest = this.rowData;

    assertNonNullable(payoutRequest.current_hourly_rate, 'current_hourly_rate');

    await this.workingHoursDialogService.openDialog({
      input: {
        period: payoutRequest.period,
        existingHours: payoutRequest.hours,
        isReadonly: true, // Always readonly in admin view
        isExistingRequest: true,
        hourlyPaymentRate: payoutRequest.current_hourly_rate,
        existingRequestPeriods: [], // Empty array since this is for viewing existing request
      },
    });
  }
}
